import {
  fontScaleLevelToRatio,
  getContent,
  getImageSrc,
  isTextBlock,
  type TextBlock,
} from "~/src/novel/core";
import { FontSheet, getFontFamily } from "~/src/novel/style";
import type { AvailableLanguages } from "~/src/api";
import { nanoid } from "nanoid";

export const hasRichText = (block: TextBlock) => {
  if (!Array.isArray(block.content))
    return (
      block.textColor ||
      block.backgroundColor ||
      block.fontItalic ||
      block.fontUnderline ||
      block.fontBold ||
      block.fontStrike ||
      block.fontAppearance ||
      block.href ||
      block.fontScaleLevel !== undefined ||
      block.textAlign ||
      block.footnote
    );
  return true;
};

export const getComputedStyles = (
  block: MaybeRefOrGetter<TextBlock>,
  noFont?: MaybeRefOrGetter<boolean | undefined>,
  language: MaybeRefOrGetter<AvailableLanguages | undefined> = () => "en",
  inline = false,
) => {
  const fontFamily = computed(() => {
    if (toValue(noFont)) return undefined;
    const textBlock = toValue(block);
    const mapped = getFontFamily(textBlock.fontAppearance, toValue(language));
    if (!mapped) return undefined;
    return `"${mapped.value}", ${mapped.font_family}`;
  });

  const fontRatio = computed(() => {
    if (toValue(noFont)) return undefined;
    const textBlock = toValue(block);
    if (!textBlock.fontAppearance)
      return fontScaleLevelToRatio(textBlock.fontScaleLevel);
    const mapped = getFontFamily(textBlock.fontAppearance, toValue(language));
    if (!mapped) return fontScaleLevelToRatio(textBlock.fontScaleLevel);
    return (
      (fontScaleLevelToRatio(textBlock.fontScaleLevel) ?? 0) +
        (mapped.baseFontRatio ?? 0) || undefined
    );
  });

  const style = computed(() => {
    const textBlock = toValue(block);
    return {
      "--text-font-ratio": fontRatio.value,
      color: textBlock.textColor,
      backgroundColor: textBlock.backgroundColor,
      fontFamily: fontFamily.value,
    };
  });

  const classObj = computed(() => {
    const textBlock = toValue(block);
    return {
      "novel-bold": textBlock.fontBold,
      "novel-italic": textBlock.fontItalic,
      "novel-underline": textBlock.fontUnderline,
      "novel-strike": textBlock.fontStrike,

      "novel-text-left": textBlock.textAlign === "left" && !inline,
      "novel-text-right": textBlock.textAlign === "right" && !inline,
      "novel-text-center": textBlock.textAlign === "center" && !inline,
      "novel-text-justify": textBlock.textAlign === "justify" && !inline,
    };
  });

  return {
    fontRatio,
    fontFamily,
    style,
    classObj,
  };
};

// BELOW IS UNUSED, logic for web-component-based render //

const getElementForBlock = (block: TextBlock, inline = false) => {
  let elem: HTMLElement;
  switch (block.type) {
    case "image":
      const image = document.createElement("novel-image");
      image.setAttribute("src", getImageSrc(block));
      return image;
    case "scene_break":
      elem = document.createElement("novel-scene-break");
      if ("textColor" in block)
        elem.style.setProperty("background-color", block.textColor!.toString());
      return elem;
    case "blockquote":
      elem = document.createElement("novel-blockquote");
      break;
    default:
      if (inline) elem = document.createElement("novel-rich-text");
      else elem = document.createElement("novel-paragraph");
  }

  if (block.fontBold) elem.setAttribute("b", "");
  if (block.fontItalic) elem.setAttribute("i", "");
  if (block.fontUnderline) elem.setAttribute("u", "");
  if (block.fontStrike) elem.setAttribute("s", "");
  if (block.textAlign) elem.setAttribute("al", block.textAlign);
  if (block.paragraphId) elem.setAttribute("id", block.paragraphId);
  if (block.href) elem.setAttribute("l", block.href);
  if (block.fontScaleLevel)
    elem.style.setProperty(
      "--text-font-ratio",
      fontScaleLevelToRatio(block.fontScaleLevel)!.toString(),
    );

  if ("textColor" in block) elem.setAttribute("c", block.textColor!.toString());
  if ("backgroundColor" in block)
    elem.setAttribute("bg", block.backgroundColor!.toString());

  return elem;
};

function renderText(input: TextBlock) {
  const elem = getElementForBlock(input);

  const renderChildren = (textBlock: TextBlock) => {
    let elems = [];

    const blockChildren = getContent(textBlock);
    let content: (string | TextBlock)[];
    if (typeof blockChildren === "string") {
      content = [blockChildren];
    } else content = blockChildren;

    for (const block of content) {
      if (isTextBlock(block)) {
        if (!hasRichText(block)) {
          const text = document.createTextNode(block.content as string);
          elems.push(text);
          continue;
        }

        const currElem = getElementForBlock(block, true);

        const children = renderChildren(block);
        children.forEach((child) => currElem.appendChild(child));

        elems.push(currElem);
      } else {
        elems.push(document.createTextNode(block));
      }
    }

    return elems;
  };

  const children = renderChildren(input);
  children.forEach((child) => elem.appendChild(child));

  return elem;
}

export function render(input: string | TextBlock[]) {
  const collection = [];

  if (typeof input === "string") {
    const node = document.createElement("novel-paragraph");
    node.innerText = input;
    return [node];
  }

  for (const block of input) {
    if (isTextBlock(block)) {
      if (block.type === "text") {
        collection.push(renderText(block));
        continue;
      }

      const currElem = getElementForBlock(block);

      if (block.type === "image") {
        collection.push(currElem);
        continue;
      }

      const content = getContent(block);

      const children = render(content);
      children.forEach((node) => currElem!.appendChild(node));
      collection.push(currElem);
    } else {
      const node = document.createElement("novel-paragraph");
      node.innerText = block;
      collection.push(node);
    }
  }

  return collection;
}
